import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Radio } from 'antd';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import {
  IMusselLineDetail,
  IMusselLineResource,
} from '../../../entities/farms.entities';
import {
  toMillisecond,
  toSecond,
  parseImageUrlToFileItem,
  isEmailFormat,
  numToStr,
  getUnitOfSpecie,
} from '../../../lib/common.helpers';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Feedback,
  Input,
  Paragrapgh,
  RadioButton,
  Spinner,
  Subtitle,
  Title,
} from '../../shared';
import { toSecTimeTracks } from '../../shared/input/TimeTrackInput';
import SignatureCanvas from 'react-signature-canvas';
import { showFeedback } from '../../../store/ui/ui.actions';
import { sendSingleRequest } from '../../../apis';
import {
  loadFarmsData,
  loadQueueData,
} from '../../../store/farms/farms.actions';
import { IFileWall, ITimeRange } from '../../../entities/general.entities';
import { loadInventories } from '../../../store/inventories/inventories.actions';
import { selectLang } from '../../../store/ui/ui.selector';
import { translate, transUnit } from '../../../lib/lang.helper';
import { ICustomValue } from '../../../entities/automation.entities';
import {
  SelectDetailedBudgetEnabled,
  SelectFarmsByFarmID,
  SelectHarvestFields,
  SelectIsBudgetVisible,
  SelectProfileByFarmID,
  SelectSettingsByFarmID,
  SelectUtilsByFarmID,
} from '../../../store/extra/extra.selector';
import { IInventorySum } from '../../../entities/inventory.entities';
import { divideEqually } from '../../../lib/common.helpers';
import { MusselHarvestReseedForm } from './HarvestReseedForm';
import { IExpenseRequest } from '../../../entities/budget.entities';
import { initialBudgetForm } from '../../../lib/form.helpers';
import OperatorBudgetForm from '../OperatorBudgetForm';
import MoreFieldsForm from './MoreFieldsForm';
import CustomValuesForm from '../../farm-util/CustomValuesForm';
import ReturningInventoriesForm from './ReturningInventoriesForm';
import HarvestBatchSelect, { HarvestGroupLensForm } from './HarvestBatchSelect';
import FilesWall from '../../shared/pictures-wall/FilesWall';
import { selectAccount } from '../../../store/auth/auth.selector';
import { TaggingManageForm } from '../../lines/LineTagButton';
import { loadTags } from '../../../store/automation/automation.actions';
import HarvestBagsForm from './HarvestBagsForm';
import TaxonomyGroupsForm from '../../farmform/TaxonomyGroupsForm';

interface IHarvestSpat {
  spat_storage_id: number;
  amount: number | null;
  line_length: number | null;
}

interface IHarvestGroup {
  batch_seed_id: number;
  amount?: number;
  line_length?: number;
}

interface IBag {
  quantity?: number;
  capacity?: number;
}

interface IFormData {
  is_final: boolean;
  is_full: boolean;
  is_sold?: boolean;
  amount: number | null;
  complete_date: number;
  company: string | null;
  harvest_number: string | null;
  tag_color: string | null;
  port_of_unload: string | null;
  crop_owner: string | null;
  growing_area: string | null;
  delivered_to: string | null;
  packhouse: string | null;
  shell_length: number | null;
  shell_length_max: number | null;
  shell_condition: string | null;
  line_length: number | null;
  fill_percent?: number;
  direction: string | null;
  mussel_type: string | null;
  mussels: number | null;
  meat_yield: number | null;
  blues: number | null;
  marine_waste: string | null;
  backbone_ok: boolean;
  backbone_replace: boolean;
  lights_ids_in_place: boolean;
  flotation_on_farm: boolean;
  rope_bags_quantity: number | null;
  product_left_on_line: string | null;
  harvester_name: string | null;
  signature?: string | null;
  comment: string | null;

  image_pdf_files?: IFileWall[];

  stripped_reason: string | null;

  time_tracks?: ITimeRange[];
  boat_id?: number | null;
  inventories?: IInventorySum[];
  harvest_spats?: IHarvestSpat[];
  custom_values?: ICustomValue[];
  harvest_groups?: IHarvestGroup[];
  harvest_bags?: IBag[];

  income: number | null;
  income_details?: IExpenseRequest[];
  cost?: number;
  expenses?: IExpenseRequest[];

  tagged_ids?: number[] | null;
  taxonomy_group_ids: number[];
}

const defaultForm: IFormData = {
  is_final: true,
  amount: null,
  income: null,
  complete_date: moment().toDate().getTime(),
  company: null,
  harvest_number: null,
  tag_color: null,
  port_of_unload: null,
  crop_owner: null,
  growing_area: null,
  delivered_to: null,
  packhouse: null,
  shell_length: null,
  shell_length_max: null,
  shell_condition: null,
  line_length: null,
  direction: null,
  mussel_type: null,
  mussels: null,
  meat_yield: null,
  blues: null,
  marine_waste: null,
  backbone_ok: false,
  backbone_replace: false,
  lights_ids_in_place: false,
  flotation_on_farm: false,
  rope_bags_quantity: null,
  product_left_on_line: null,
  harvester_name: null,
  signature: null,
  comment: null,
  image_pdf_files: [],
  stripped_reason: null,
  time_tracks: [],
  harvest_bags: [{ capacity: 500 }],
  is_full: false,
  taxonomy_group_ids: [],
};

const HarvestSpatsView: React.FC<{
  spats: IMusselLineResource['growing_spats'];
  data: IHarvestSpat[];
  setData: (d: IHarvestSpat[]) => void;
  unitType: 'METRIC' | 'IMPERIAL' | null | undefined;
}> = ({ spats, data, setData, unitType }) => {
  const lang = useSelector(selectLang);

  const selectHarvestSpats = (v: string[]) =>
    setData(
      v.map(
        i =>
          data.find(x => x.spat_storage_id === Number(i)) ?? {
            spat_storage_id: Number(i),
            amount: null,
            line_length: null,
          },
      ),
    );
  const updateRow = (
    id: number,
    key: 'amount' | 'line_length',
    val: string,
  ) => {
    let tmp = [...data];
    const i = tmp.findIndex(x => x.spat_storage_id === id);
    tmp[i][key] = val.length > 0 ? Number(val) : null;
    setData(tmp);
  };

  return (
    <div>
      <Dropdown
        label={translate(lang, '_sel_harvested_spats')}
        value={data.map(x => x.spat_storage_id.toString()) as any}
        options={
          spats?.map(x => ({
            id: x.id.toString(),
            value: x.id.toString(),
            label: x.source,
          })) ?? []
        }
        onChange={(v: any) => selectHarvestSpats(v)}
        mode='multiple'
        className='mb-17'
      />
      {data.map((x, k) => (
        <div
          className='d-flex mb-7 pb-7 align-center'
          key={k}
          style={{ borderBottom: '1px solid #efefef' }}
        >
          <div className='mr-7' style={{ fontWeight: 'bold' }}>
            {spats?.find(y => y.id === x.spat_storage_id)?.source ?? ''}
          </div>
          <div className='mr-7'>
            <Input
              label={translate(lang, 'Harvested Amount')}
              type='number'
              value={numToStr(x.amount)}
              onChange={e =>
                updateRow(x.spat_storage_id, 'amount', e.target.value)
              }
              unit={getUnitOfSpecie(unitType, 'weight_kg')}
            />
          </div>
          <div className='ml-7'>
            <Input
              label={translate(lang, 'Harvested length')}
              value={numToStr(x.line_length)}
              onChange={e =>
                updateRow(x.spat_storage_id, 'line_length', e.target.value)
              }
              type='number'
              unit={getUnitOfSpecie(unitType, 'distance_m')}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

interface PartialDialogProps {
  onConfirm: (d: any) => void;
}

const ConfirmPartialDialog = ({ onConfirm }: PartialDialogProps) => {
  const lang = useSelector(selectLang);

  return (
    <div className='confirm-partial-dialog'>
      <div className='mt-32 mb-32'>
        <Title size={6} color='default' align='center' fontWeight={500}>
          {translate(lang, '_harvested_entire_line')}
        </Title>
      </div>
      <div className='d-flex justify-between mt-16 mb-32'>
        <Button
          width={'wide'}
          size={2}
          type='fill'
          color='green'
          className='mr-16'
          onClick={() => onConfirm(false)}
        >
          {translate(lang, '_yes_line_now_empty')}
        </Button>
        <Button
          width={'wide'}
          size={2}
          type='fill'
          color='blue'
          className='ml-16'
          onClick={() => onConfirm(true)}
        >
          {translate(lang, '_crop')}
        </Button>
      </div>
    </div>
  );
};

interface SCDProps {
  amount: number;
  onConfirm: () => void;
  onCancel: () => void;
}

const SubmitConfirmDialog = ({ amount, onConfirm, onCancel }: SCDProps) => {
  const lang = useSelector(selectLang);

  return (
    <div className='confirm-partial-dialog'>
      <div className='mt-32 mb-32'>
        <Paragrapgh size={6} color='default' align='left' fontWeight={500}>
          {translate(lang, '_harvest_confirm_dialog_1')}
          <br />
          {translate(lang, '_harvest_confirm_dialog_2')}
          <br />
          {translate(
            lang,
            '_confirm_value_correct',
            amount,
          )}
        </Paragrapgh>
      </div>
      <div className='d-flex justify-between mt-16 mb-32'>
        <Button
          width={'wide'}
          size={2}
          type='fill'
          color='green'
          className='mr-16'
          onClick={onCancel}
        >
          {translate(lang, 'Go back and correct')}
        </Button>
        <Button
          width={'wide'}
          size={2}
          type='fill'
          color='blue'
          className='ml-16'
          onClick={onConfirm}
        >
          {translate(lang, 'Confirm the value')}
        </Button>
      </div>
    </div>
  );
};

const lengthText = (type: IMusselLineResource['line_type']) =>
  type === 'LONGLINE' ? 'Harvested length' : 'Number of droppers';

interface Props {
  visible: boolean;
  title?: string;
  lineData: IMusselLineResource;
  data?: any;
  onCancel: () => void;
  onConfirm: (data: any) => void;
  overConfirm?: (data: any) => Promise<any>;
  onlyView?: boolean;
  warningMessage?: string;
  lineChangeable?: boolean;
  updateID?: number;
}

const MusselHarvestModal = ({
  visible,
  title,
  lineData,
  data,
  onCancel,
  onConfirm,
  overConfirm,
  onlyView,
  warningMessage,
  lineChangeable,
  updateID,
}: Props) => {
  const dispatch = useDispatch<any>();

  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;
  const profile = SelectProfileByFarmID(lineData.farm_id);
  const friendEmails = SelectSettingsByFarmID(lineData.farm_id)?.friend_emails;
  const farmsData = SelectFarmsByFarmID(lineData.farm_id);
  const farmUtils = SelectUtilsByFarmID(lineData.farm_id);
  const customFields = SelectHarvestFields(lineData.farm_id);
  const showBudget = SelectIsBudgetVisible(lineData.farm_id);
  const detailedBudgetEnabled = SelectDetailedBudgetEnabled(lineData.farm_id);

  const port_of_unloads = farmUtils.filter(x => x.type === 'port_of_unload'),
    delivered_tos = farmUtils.filter(x => x.type === 'delivered_to'),
    packhouses = farmUtils.filter(x => x.type === 'packhouse');

  const [curFarm, setCurFarm] = useState(
    farmsData.find(x => x.id === lineData.farm_id),
  );
  const [curLine, setCurLine] = useState(lineData);
  const [errorMsg, setErrorMsg] = useState<string>();
  const [errors, setErrors] = useState<any>();

  const { currentSeed, growingLength, lastHarvest } = useMemo(
    () => ({
      currentSeed: curLine.growing_cycle?.main_seed,
      growingLength: curLine.growing_cycle?.current_line_length ?? 0,
      lastHarvest: curLine.growing_cycle?.last_harvest,
    }),
    [curLine],
  );

  const [disabled, setDisabled] = useState(false);
  const [isPartial, setIsPartial] = useState<boolean | null>(null);
  const [visibleConfirm, setVisibleConfirm] = useState(false);
  const [formData, setFormData] = useState<IFormData>(() => ({
    ...defaultForm,
    harvester_name: profile?.name ?? null,
    growing_area:
      curFarm?.growing_area ?? `${curLine.farm_name} - ${curLine.line_name}`,
    boat_id: data?.boat_id ?? profile?.current_boat?.id,
    inventories: lineData.growing_cycle?.inventories_sum,
    crop_owner:
      curFarm && curFarm.owners && curFarm.owners.length > 0
        ? curFarm.owners[0].title
        : null,
    company: profile?.company_name ?? null,
    port_of_unload:
      port_of_unloads && port_of_unloads.length > 0
        ? port_of_unloads[0].name
        : defaultForm.port_of_unload,
    delivered_to:
      lineData.delivered_to ??
      (delivered_tos && delivered_tos.length > 0
        ? delivered_tos[0].name
        : defaultForm.delivered_to),
    packhouse:
      packhouses && packhouses.length > 0
        ? packhouses[0].name
        : defaultForm.packhouse,
    is_final: lineData.growing_cycle?.last_harvest
      ? !!lineData.growing_cycle.last_harvest.is_final
      : defaultForm.is_final,
    harvest_groups: data?.harvest_groups ? data.harvest_groups : null,
    harvest_bags: data?.harvest_bags ?? defaultForm.harvest_bags,
    fill_percent: lineData.fill_percent ?? undefined,
    income_details: detailedBudgetEnabled
      ? [{ name: '', quantity: null, unit_price: null, amount: null }]
      : undefined,
    expenses: detailedBudgetEnabled
      ? [{ name: '', quantity: null, unit_price: null, amount: null }]
      : undefined,
    tagged_ids: data?.tagged_ids ?? null,
  }));
  const [reqPdf, setReqPdf] = useState(data?.require_pdf ?? false);
  const [emails, setEmails] = useState<string>(
    data?.request_emails?.join(',') ?? friendEmails?.join(',') ?? '',
  );

  const signatureCanvas = useRef<SignatureCanvas | null>();
  const [sigCanLoaded, setSigCanLoaded] = useState(false);
  const [advancedLine, setAdvancedLine] = useState<IMusselLineDetail>();
  const [harvestSpats, setHarvestSpats] = useState<IHarvestSpat[]>([]);
  const [trackMode, setTrackMode] = useState(false);
  const [reseedFarms, setReseedFarms] = useState(curFarm ? [curFarm] : []);
  const [reseedForm, setReseedForm] = useState<any>();
  const [loading, setLoading] = useState(false);

  const handleLineChange = (v: string) => {
    if (!curFarm) return;
    const line = curFarm.lines.find(x => x.id === Number(v)) as
      | IMusselLineResource
      | undefined;
    if (!line?.growing_cycle) return;
    setCurLine(line);
    setFormData(prv => ({
      ...prv,
      inventories: line.growing_cycle?.inventories_sum,
      is_final: line.growing_cycle?.last_harvest
        ? line.growing_cycle.last_harvest.is_final
        : prv.is_final,
    }));
  };

  const updateHarvestSpats = (d: IHarvestSpat[]) => {
    setHarvestSpats(d);
    const as = d.reduce((p, c) => p + (c.amount ?? 0), 0);
    updateForm('amount', as);
    const ls = d.reduce((p, c) => p + (c.line_length ?? 0), 0);
    updateForm('line_length', ls);
  };
  const updateHarvestBags = (bags: IBag[]) => {
    const totalBags = bags.reduce((p, c) => p + (c.quantity ?? 0), 0);
    const totalAmount =
      bags.length <= 0
        ? formData.amount
        : bags.reduce((p, c) => p + (c.quantity ?? 0) * (c.capacity ?? 0), 0);
    setFormData(prv => ({
      ...prv,
      harvest_bags: bags,
      amount: totalAmount,
    }));
  };
  const toggleTrackMode = async (c: boolean) => {
    if (c && (!advancedLine || advancedLine.id !== curLine.id)) {
      setLoading(true);
      const response = await sendSingleRequest(
        { with_harvest_spats: true, track_harvest_id: updateID },
        'GET',
        `api/farm/line/lines/${curLine.id}`,
        true,
      );
      setLoading(false);
      if (response.status) {
        setAdvancedLine(response.data);
      }
    }
    setTrackMode(c);
  };
  const updateForm = (key: keyof IFormData, value: any) => {
    if (
      ['amount', 'income', 'shell_length', 'shell_length_max'].includes(key)
    ) {
      value = value.length <= 0 ? null : Number(value);
      setFormData(prv => ({ ...prv, [key]: value }));
    } else if (key === 'is_final' && !value) {
      setFormData(prv => ({
        ...prv,
        is_final: false,
        stripped_reason: null,
      }));
    } else if (key === 'line_length') {
      value = value.length <= 0 ? null : Number(value);
      setFormData(prv => ({ ...prv, line_length: value }));
    } else if (key === 'fill_percent') {
      value = value.length <= 0 ? null : Number(value);
      setFormData(prv => ({ ...prv, fill_percent: value }));
    } else if (key === 'harvest_groups') {
      const lenSum =
        value?.reduce(
          (sum: number, x: IHarvestGroup) => sum + (x.line_length ?? 0),
          0,
        ) ?? 0;
      setFormData(prv => ({
        ...prv,
        harvest_groups: value,
        line_length: lenSum > 0 ? lenSum : null,
      }));
    } else {
      if (typeof value === 'string' && value.length <= 0) {
        value = null;
      }
      setFormData(prv => ({ ...prv, [key]: value }));
    }
    setErrors((prv: any) => ({ ...(prv ?? {}), [key]: null }));
  };
  const validForm = () => {
    if (!updateID && !curLine.growing_cycle) {
      setErrorMsg('_line_empty');
      return null;
    }
    if (!updateID && !currentSeed) {
      setErrorMsg('_line_empty');
      return null;
    }
    const line = curFarm?.lines.find(y => y.id === curLine.id) as
      | IMusselLineResource
      | undefined;
    if (
      updateID &&
      !data.is_full !== isPartial &&
      line?.last_harvest?.id !== updateID
    ) {
      setErrorMsg('_impossible_harvest_update');
      return null;
    }
    if (
      !updateID &&
      isPartial === true &&
      formData.line_length &&
      (growingLength ?? 0) < formData.line_length
    ) {
      const es = !formData.line_length
        ? translate(lang, '_input_harvested_length')
        : translate(
            lang,
            'Maximum available harvest length is %s',
            `${growingLength}${getUnitOfSpecie(unitType, 'distance_m')}`,
          );
      setErrorMsg(es);
      setErrors((prv: any) => ({ ...(prv ?? {}), line_length: es }));
      return null;
    }
    if (
      !updateID &&
      isPartial === false &&
      formData.line_length &&
      Math.abs(formData.line_length - growingLength!) > 0.0001 &&
      !window.confirm(
        translate(
          lang,
          '_harvest_full_confirm',
          growingLength,
          getUnitOfSpecie(unitType, 'distance_m'),
          formData.line_length,
          getUnitOfSpecie(unitType, 'distance_m'),
        ),
      )
    ) {
      return null;
    }
    if (
      formData.is_final &&
      (formData.amount === null || formData.amount <= 0)
    ) {
      const es =
        formData.amount === null
          ? translate(lang, '_enter_harvested_amt')
          : translate(lang, '_invalid_harvest_amount_length');
      setErrorMsg(es);
      setErrors((prv: any) => ({ ...(prv ?? {}), amount: es }));
      return null;
    }
    if (
      formData.shell_length_max !== null &&
      (formData.shell_length === null ||
        formData.shell_length > formData.shell_length_max)
    ) {
      const es = translate(lang, '_invalid_shell_length_range');
      setErrorMsg(es);
      setErrors((prv: any) => ({ ...(prv ?? {}), shell_length: es }));
      return null;
    }
    if (emails && emails.split(',').some(x => !isEmailFormat(x))) {
      setErrorMsg('_invalid_emails');
      return null;
    }
    if (reseedForm) {
      if (!formData.shell_length) {
        setErrorMsg('_input_shell_length');
        return null;
      }
      if (!reseedFarms || reseedFarms.length === 0) {
        setErrorMsg('Please select immediate seed farm');
        return null;
      }
      if (reseedForm.lines.some((x: any) => !x.line_length)) {
        setErrorMsg('Please input line length for all immediate seed lines');
        return null;
      }
    }
    if (
      formData.is_sold &&
      !window.confirm(translate(lang, '_sell_spat_confirm'))
    ) {
      return null;
    }
    const divider = formData.harvest_groups?.length ?? 0;
    let res = {
      ...formData,
      seeding_id: data?.seeding_id ?? currentSeed?.id,
      season_name: data?.season_name ?? currentSeed?.season_name,
      line_id: curLine.id,
      complete_date: toSecond(formData.complete_date),
      is_full: !isPartial,
      reseed: reseedForm,
      require_pdf: reqPdf,
      request_emails: emails.split(','),
      bags_quantity:
        formData.harvest_bags?.reduce((p, c) => p + (c.quantity ?? 0), 0) ??
        undefined,
      harvest_groups: formData.harvest_groups?.map((x, i) => ({
        batch_seed_id: x.batch_seed_id,
        amount: divideEqually(formData.amount ?? 0, divider, i),
        line_length:
          x.line_length ?? divideEqually(formData.line_length ?? 0, divider, i),
      })),
      income_details: formData.income_details?.filter(
        x => x.name && !!x.amount,
      ),
      expenses: formData.expenses?.filter(x => x.name && !!x.amount),
    };
    if (trackMode && harvestSpats.length > 0) {
      if (harvestSpats.some(x => !x.amount || !x.line_length)) {
        setErrorMsg('_harvested_original_spats_valid');
        setErrors((prv: any) => ({
          ...(prv ?? {}),
          amount: translate(lang, '_invalid_harvest_amount_length'),
        }));
        return null;
      }
      res.harvest_spats = harvestSpats;
    }
    if (!res.income) res.income = 0;
    if (
      !updateID &&
      signatureCanvas.current &&
      !signatureCanvas.current.isEmpty()
    ) {
      res.signature = signatureCanvas.current.toDataURL();
    } else {
      delete res.signature;
    }
    if (res.time_tracks && res.time_tracks.length > 0) {
      const tt = toSecTimeTracks(res.time_tracks);
      if (!tt) {
        setErrorMsg('_invalid_time_range');
        return null;
      }
      res.time_tracks = tt;
    }
    if (
      !updateID &&
      isPartial &&
      formData.line_length &&
      formData.line_length === growingLength
    ) {
      if (!window.confirm(translate(lang, '_harvest_len_partial_confirm'))) {
        return null;
      }
      res.is_full = true;
    }
    const cvs = formData.custom_values?.filter(x => !!x.field_val) ?? [];
    if (cvs.length > 0) {
      res.custom_values = cvs;
    } else {
      res.custom_values = undefined;
    }
    setErrors(undefined);
    return res;
  };
  const handleConfirmClick = async () => {
    const form = validForm();
    if (!form) return;

    setDisabled(true);
    if (overConfirm) {
      const res = await overConfirm(form);
      if (!res.status) {
        setErrorMsg(res.data?.message ?? 'Request failed');
        if (res.data?.errors) setErrors(res.data.errors);
        setDisabled(false);
      }
      return;
    }
    const res = !updateID
      ? await sendSingleRequest(
          form,
          'POST',
          'api/farm/line/harvest-complete',
          true,
        )
      : await sendSingleRequest(
          form,
          'POST',
          `api/farm/line/harvest-update/${updateID}`,
          true,
        );
    if (res.status) {
      if (form.tagged_ids) {
        dispatch(loadTags());
      }
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, res.data?.message ?? 'Success'),
        }),
      );
      dispatch(loadFarmsData());
      dispatch(loadQueueData());
      if (form.inventories) {
        dispatch(loadInventories());
      }
      onConfirm(res.data);
    } else {
      setErrorMsg(res.data?.message ?? 'Request failed');
      if (res.data?.errors) setErrors(res.data.errors);
      setDisabled(false);
    }
  };

  const farmOptions = farmsData.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: `${x.name} - ${x.farm_number}`,
  }));
  const lineOptions = curFarm?.lines
    .filter(x => !!x.growing_cycle && x.crop_type === 'MUSSEL')
    .map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: x.line_name,
    }));

  useEffect(() => {
    if (data) {
      let tmp = { ...data };
      if (tmp.complete_date) {
        tmp.complete_date = toMillisecond(tmp.complete_date);
      }
      if (tmp.image_pdf_files) {
        tmp.image_pdf_files = tmp.image_pdf_files.map((x: any) =>
          typeof x === 'object' && x !== null ? x : parseImageUrlToFileItem(x),
        );
      }
      if (tmp.pdf_files) {
        tmp.pdf_files = tmp.pdf_files.map((x: any) =>
          typeof x === 'object' && x !== null ? x : parseImageUrlToFileItem(x),
        );
      }
      if (tmp.is_full !== undefined && tmp.is_full !== null) {
        setIsPartial(!tmp.is_full);
      }
      if (data.time_tracks) {
        tmp.time_tracks = data.time_tracks.map((x: any) => ({
          start_time: toMillisecond(x.start_time),
          finish_time: toMillisecond(x.finish_time),
        }));
      }
      if (showBudget) {
        tmp = { ...tmp, ...initialBudgetForm(detailedBudgetEnabled, data) };
      }
      if (tmp.harvest_bags === undefined || tmp.harvest_bags === null) {
        tmp.harvest_bags = defaultForm.harvest_bags;
      }
      tmp.taxonomy_group_ids =
        data.taxonomy_group_ids && data.taxonomy_group_ids.length > 0
          ? data.taxonomy_group_ids
          : (data.taxonomy_groups ?? []).map((x: { id: number }) => x.id);
      setFormData(prv => ({ ...prv, ...tmp }));

      if (data.harvest_spats && data.harvest_spats.length > 0) {
        setHarvestSpats(
          data.harvest_spats.map((x: any) => ({
            ...x,
            spat_storage_id: x.spat_storage_id ?? x.spat_storage.id,
          })),
        );
        toggleTrackMode(true);
      }
      if (data.reseed && data.reseed.lines && data.reseed.lines.length > 0) {
        const reseedLineIds = new Set(
          data.reseed.lines.map((z: any) => z.line_id),
        );
        const tmpFarms = farmsData.filter(farm =>
          farm.lines?.some(line => reseedLineIds.has(line.id)),
        );
        setReseedFarms(tmpFarms);
        setReseedForm(data.reseed);
      }
    }
  }, [data, showBudget, detailedBudgetEnabled]);

  useEffect(() => {
    if (sigCanLoaded && data && data.signature) {
      signatureCanvas.current?.fromDataURL(data.signature);
    }
  }, [data, sigCanLoaded, signatureCanvas.current]);

  useEffect(() => {
    setFormData(prv => ({
      ...prv,
      line_length: prv.line_length ? prv.line_length : growingLength,
    }));
  }, [growingLength]);

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={650}
    >
      <div className='wrap'>
        {loading && (
          <div className='loader'>
            <Spinner />
          </div>
        )}
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, title ?? 'Mussel Harvest Form')}
          </Subtitle>
        </div>
        <div className='harvest-modal'>
          {lineChangeable && (
            <div className='mb-17 d-flex'>
              <div className='w-100 mr-7'>
                <Dropdown
                  label={translate(lang, 'Select farm')}
                  options={farmOptions}
                  value={curFarm?.id.toString()}
                  onChange={v =>
                    setCurFarm(farmsData.find(x => x.id === Number(v)))
                  }
                />
              </div>
              <div className='w-100 ml-7'>
                <Dropdown
                  label={translate(lang, 'Select line')}
                  options={lineOptions ?? []}
                  value={
                    !!lineOptions &&
                    lineOptions?.some(x => Number(x.id) === curLine.id)
                      ? curLine.id.toString()
                      : curLine.line_name
                  }
                  onChange={handleLineChange}
                />
              </div>
            </div>
          )}
          {!!warningMessage && (
            <div className='header-alert'>{warningMessage}</div>
          )}
          {!!errorMsg && (
            <Feedback
              message={translate(lang, errorMsg)}
              type={'error'}
              theme='light'
              position={10}
              isGlobal
              onClose={() => setErrorMsg(undefined)}
            />
          )}
          {isPartial === null ? (
            <ConfirmPartialDialog onConfirm={x => setIsPartial(x)} />
          ) : visibleConfirm ? (
            <SubmitConfirmDialog
              amount={Number(formData.amount)}
              onConfirm={handleConfirmClick}
              onCancel={() => setVisibleConfirm(false)}
            />
          ) : (
            <div>
              <TaxonomyGroupsForm
                className='mb-17 mt-17'
                value={formData.taxonomy_group_ids}
                onChange={ids => updateForm('taxonomy_group_ids', ids)}
                disabled={onlyView}
              />
              {!updateID && curLine.growing_cycle && (
                <ReturningInventoriesForm
                  type='MUSSEL'
                  farmID={curLine.farm_id}
                  cycleData={curLine.growing_cycle}
                  shellSize={formData.shell_length}
                  form={formData.inventories}
                  onChange={d =>
                    setFormData(prv => ({ ...prv, inventories: d }))
                  }
                  pastInventories={data?.inventories}
                />
              )}
              <div className='pl-17 mb-32 mt-7 pr-17'>
                <Subtitle
                  size={4}
                  fontWeight={700}
                  align='left'
                  color='black-3'
                >
                  {translate(lang, '_harvested_entire_line')}
                </Subtitle>
                <Radio.Group
                  onChange={e => {
                    setIsPartial(e.target.value === 'P');
                    if (e.target.value === 'F' && growingLength) {
                      updateForm('line_length', growingLength);
                    }
                  }}
                  value={isPartial ? 'P' : 'F'}
                  disabled={onlyView}
                >
                  <RadioButton
                    label={translate(lang, '_yes_line_now_empty')}
                    value='F'
                    className='mt-7'
                  />
                  <RadioButton
                    label={translate(lang, '_crop')}
                    value='P'
                    className='mt-7'
                  />
                </Radio.Group>
              </div>
              <div className='mb-17'>
                {!!lastHarvest && lastHarvest.is_final == formData.is_final && (
                  <div>
                    <Title
                      size={6}
                      color='black'
                      align='center'
                      fontWeight={500}
                    >
                      {translate(
                        lang,
                        lastHarvest.is_final
                          ? `This is final harvest continuation`
                          : `This is intermediate harvest continuation`,
                      )}
                    </Title>
                  </div>
                )}
                <div className='pl-17 mb-17 mt-7 pr-17'>
                  <Radio.Group
                    onChange={e => {
                      if (e.target.value === 'SOLD') {
                        updateForm('is_sold', true);
                        updateForm('is_final', false);
                      } else {
                        updateForm('is_sold', false);
                        updateForm('is_final', e.target.value === 'FINAL');
                      }
                    }}
                    value={
                      formData.is_sold
                        ? 'SOLD'
                        : !formData.is_final
                          ? 'MIDDLE'
                          : 'FINAL'
                    }
                    disabled={onlyView || (updateID && data?.is_sold)}
                  >
                    <RadioButton
                      label={translate(lang, '_radio_final')}
                      value='FINAL'
                      className='mt-7'
                    />
                    <RadioButton
                      label={translate(lang, '_radio_intermediate')}
                      value='MIDDLE'
                      className='mt-7'
                    />
                    <RadioButton
                      label={translate(lang, '_spat_sold')}
                      value='SOLD'
                      className='mt-7'
                    />
                  </Radio.Group>
                </div>
                {!updateID && !formData.is_final && !formData.is_sold && (
                  <MusselHarvestReseedForm
                    farmsData={farmsData}
                    farms={reseedFarms}
                    setFarms={setReseedFarms}
                    form={reseedForm}
                    setForm={setReseedForm}
                    farmId={curLine.farm_id}
                    currentStage={currentSeed?.stage}
                    disabled={onlyView || disabled}
                  />
                )}
              </div>
              <div className='mt-32 mb-17'>
                <div className={errors?.amount ? 'invalid-form' : ''}>
                  {!formData.is_sold &&
                    !!profile?.account?.track_original_spat && (
                      <CheckboxButton
                        label={translate(lang, 'Track Original Spats')}
                        className='mb-12'
                        onChange={e => toggleTrackMode(e.target.checked)}
                        checked={trackMode}
                      />
                    )}
                  {!!trackMode && (
                    <HarvestSpatsView
                      spats={advancedLine?.growing_spats}
                      data={harvestSpats}
                      setData={updateHarvestSpats}
                      unitType={unitType}
                    />
                  )}
                  <Input
                    type='number'
                    onChange={e => updateForm('amount', e.target.value)}
                    value={numToStr(formData.amount)}
                    label={translate(lang, 'Harvest amount')}
                    unit={getUnitOfSpecie(unitType, 'weight_kg')}
                    disabled={onlyView}
                  />
                  {!!errors?.amount && (
                    <div className='invalid-feedback'>{errors?.amount}</div>
                  )}
                </div>
              </div>
              <div
                className={
                  errors?.complete_date ? 'invalid-form pb-24' : 'pb-24'
                }
              >
                <Datepicker
                  defaultValue={formData.complete_date}
                  label={translate(lang, '_harvest_complete_date')}
                  onChange={e =>
                    e && updateForm('complete_date', e.toDate().getTime())
                  }
                  disabled={onlyView}
                />
                {!!errors?.complete_date && (
                  <div className='invalid-feedback'>
                    {errors?.complete_date}
                  </div>
                )}
              </div>
              <div className='mb-17 d-flex justify-between'>
                <div className='w-100 mr-7'>
                  <Input
                    label={translate(lang, 'Shell length from')}
                    type='number'
                    onChange={e => updateForm('shell_length', e.target.value)}
                    value={numToStr(formData.shell_length)}
                    unit={getUnitOfSpecie(unitType, 'size_mm')}
                    disabled={onlyView}
                  />
                </div>
                <div className='w-100 ml-7'>
                  <Input
                    label={translate(lang, 'Shell length to')}
                    type='number'
                    onChange={e =>
                      updateForm('shell_length_max', e.target.value)
                    }
                    value={numToStr(formData.shell_length_max)}
                    unit={getUnitOfSpecie(unitType, 'size_mm')}
                    disabled={onlyView}
                  />
                </div>
              </div>
              <HarvestBagsForm
                className='mb-17'
                data={formData.harvest_bags}
                onChange={updateHarvestBags}
                disabled={onlyView}
              />
              <div
                className={errors?.line_length ? 'invalid-form mb-17' : 'mb-17'}
              >
                <Input
                  label={translate(lang, lengthText(curLine.line_type))}
                  placeholder={
                    !updateID
                      ? translate(
                          lang,
                          '%s available',
                          `${growingLength}${getUnitOfSpecie(unitType, 'distance_m')}`,
                        )
                      : ''
                  }
                  type='number'
                  onChange={e => updateForm('line_length', e.target.value)}
                  value={numToStr(formData.line_length)}
                  disabled={onlyView}
                  unit={getUnitOfSpecie(unitType, 'distance_m')}
                />
                {!!errors?.line_length && (
                  <div className='invalid-feedback'>{errors?.line_length}</div>
                )}
              </div>
              <div className='mb-17'>
                <Input
                  label={transUnit(lang, 'fill_percent', curFarm?.structure)}
                  type='number'
                  value={numToStr(formData.fill_percent)}
                  unit='%'
                  onChange={e => updateForm('fill_percent', e.target.value)}
                  disabled={onlyView}
                />
              </div>
              <div className='mb-17'>
                <Input
                  type='text'
                  onChange={e => updateForm('harvest_number', e.target.value)}
                  value={formData.harvest_number ?? ''}
                  label={`${translate(lang, 'Harvest number')}${
                    curLine.last_harvest?.harvest_number
                      ? ` (${translate(
                          lang,
                          'last value is %s',
                          curLine.last_harvest.harvest_number,
                        )})`
                      : ''
                  }`}
                  disabled={onlyView}
                />
              </div>
              <MoreFieldsForm
                type='MUSSEL'
                farmID={lineData.farm_id}
                form={formData}
                onChange={d => setFormData(d)}
                disabled={onlyView}
              />
              {(curLine.growing_cycle?.seedings.length ?? 0) > 0 &&
                !!isPartial && (
                  <div className='mb-17'>
                    <HarvestBatchSelect
                      type='MUSSEL'
                      growingSeeds={curLine.growing_cycle?.seedings ?? []}
                      values={
                        formData.harvest_groups?.map(x => x.batch_seed_id) ?? []
                      }
                      onChange={sIds =>
                        updateForm(
                          'harvest_groups',
                          sIds
                            ? sIds.map(id => ({
                                batch_seed_id: id,
                                line_length: formData.harvest_groups?.find(
                                  y => y.batch_seed_id === id,
                                )?.line_length,
                              }))
                            : undefined,
                        )
                      }
                      showAllOptions={!!updateID}
                      existingGroups={
                        updateID ? data?.harvest_groups : undefined
                      }
                    />
                    <HarvestGroupLensForm
                      type='MUSSEL'
                      lineType={curLine.line_type}
                      growingSeeds={curLine.growing_cycle?.seedings ?? []}
                      values={formData.harvest_groups}
                      onChange={vals => updateForm('harvest_groups', vals)}
                      existingGroups={
                        updateID ? data?.harvest_groups : undefined
                      }
                      className='mt-17'
                    />
                  </div>
                )}
              {!!showBudget && (
                <OperatorBudgetForm
                  detailedBudgetEnabled={detailedBudgetEnabled}
                  incomeType={detailedBudgetEnabled ? 'detailed' : 'normal'}
                  form={formData}
                  onChange={setFormData}
                  disabled={onlyView}
                />
              )}
              <CustomValuesForm
                values={formData.custom_values ?? []}
                fields={customFields}
                onChange={data =>
                  setFormData(prv => ({ ...prv, custom_values: data }))
                }
                disabled={onlyView}
              />
              <div className='pb-10'>
                <Input
                  label={translate(lang, 'Comment')}
                  type='textarea'
                  onChange={e => updateForm('comment', e.target.value)}
                  value={formData.comment ?? ''}
                  disabled={onlyView}
                />
              </div>
              <div className='pb-10'>
                <FilesWall
                  label={translate(lang, 'PDF/Images')}
                  fileList={formData.image_pdf_files}
                  onChange={v => updateForm('image_pdf_files', v)}
                  disabled={onlyView}
                />
              </div>
              <div className='pb-20'>
                <p className='mb-4 d-block paragrapgh paragrapgh--2 paragrapgh--400 paragrapgh--default'>
                  {translate(lang, 'Signature')}
                </p>
                <div
                  style={{
                    border: '1px solid #EFEFEF',
                    borderRadius: '4px',
                    height: '200px',
                  }}
                >
                  <SignatureCanvas
                    ref={r => {
                      signatureCanvas.current = r;
                      setSigCanLoaded(true);
                    }}
                    penColor='green'
                    canvasProps={{ width: 550, height: 200 }}
                  />
                </div>
              </div>
            </div>
          )}
          {!updateID &&
            !visibleConfirm &&
            isPartial !== null &&
            !formData.is_sold && (
              <div className='mb-27'>
                <CheckboxButton
                  label={translate(
                    lang,
                    '_send_me_pdf_harvest',
                  )}
                  checked={reqPdf}
                  onChange={e => setReqPdf(e.target.checked)}
                  disabled={onlyView}
                />
                {reqPdf && (
                  <div className='mt-17'>
                    <Input
                      label={translate(lang, 'Email list')}
                      placeholder={translate(
                        lang,
                        '_enter_emails_separate_comma',
                      )}
                      type='text'
                      value={emails}
                      onChange={e => setEmails(e.target.value)}
                      disabled={onlyView}
                    />
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
      {!updateID && isPartial !== null && !visibleConfirm && (
        <TaggingManageForm
          className='mb-17 mt-17'
          lineData={curLine}
          tagIDs={formData.tagged_ids}
          onChange={ids => updateForm('tagged_ids', ids)}
        />
      )}
      {isPartial !== null && !visibleConfirm && (
        <div className='modal-button d-flex justify-end align-center'>
          <Button
            width={'small'}
            size={2}
            type='fill'
            color='green'
            className='rsp-btn ml-16'
            onClick={handleConfirmClick}
            disabled={disabled || onlyView}
          >
            {translate(lang, 'Confirm')}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default MusselHarvestModal;
